import { useCallback } from "react";
import { toast } from "react-toastify";
import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";
import {
    buildGroupInfoDto,
    extractStudentGradingProfileId,
    getGradingCriteriaId,
    mapRatingSubmission
} from "../utils/InputRatingUtils";

const buildCommentSubmissionArray = ({
    students,
    comments,
    existingStudentComments,
    groupData,
    selectedEpr,
    selectedTemplate,
    mode
}) => {
    return students.reduce((acc, student) => {
        const gradingCriteriaId = getGradingCriteriaId(student, groupData, selectedEpr, selectedTemplate, mode);
        const commentKey = `${student.key}_${gradingCriteriaId}`;
        const existing = existingStudentComments?.find(
            (r) =>
                r.gradingProfileStudentId?.toString() === extractStudentGradingProfileId(student.key).toString() &&
                r.gradingCriteria?.toString() === gradingCriteriaId?.toString()
        );

        const existingComment = existing?.comment?.trim() || "";
        const newComment = (comments[commentKey] || "").trim();

        if (existingComment !== newComment) {
            acc.push({
                key: existing?.key || null,
                gradingProfileStudentId: extractStudentGradingProfileId(student.key),
                gradingCriteria: parseInt(gradingCriteriaId, 10),
                comment: newComment === "" ? null : newComment
            });
        }

        return acc;
    }, []);
};

const buildCommentUpdateOptions = (mode, groupData, selectedEpr, selectedTemplate, commentPayload, token) => {
    if (mode === "GROUPS") {
        return {
            action: "updateGroupStudentCriteriaComments",
            data: {
                groupInfoDto: buildGroupInfoDto(groupData, selectedEpr, selectedTemplate),
                contentStudentComments: commentPayload
            },
            token
        };
    } else {
        return {
            action: "updateStudentCriteriaComments",
            gradingCriteriaId: commentPayload[0]?.gradingCriteria,
            data: commentPayload,
            token
        };
    }
};

const submitComments = async ({
    students,
    comments,
    existingStudentComments,
    groupData,
    selectedEpr,
    selectedTemplate,
    token,
    mode,
    setExistingStudentComments,
    setRatingsLoaded,
    setHasChanges
}) => {
    const commentPayload = buildCommentSubmissionArray({
        students,
        comments,
        existingStudentComments,
        groupData,
        selectedEpr,
        selectedTemplate,
        mode
    });

    const options = buildCommentUpdateOptions(mode, groupData, selectedEpr, selectedTemplate, commentPayload, token);

    const response = await ElementaryProgressReportDao(options);
    if (response?.data?.payload) {
        setExistingStudentComments(response.data.payload);
        setRatingsLoaded(true);
        setHasChanges(false);
        toast.success("Comments saved.", { autoClose: 3000 });
    } else {
        toast.error("Something went wrong.", { autoClose: 3000 });
        setRatingsLoaded(true);
    }
};

const submitRatings = async ({
    ratings,
    contentItems,
    existingRatingsData,
    derivedExistingRatingsData,
    selectedEpr,
    selectedTemplate,
    userDetails,
    groupData,
    mode,
    fetchSelectedGroupInfo,
    setExistingRatingsData,
    setRatingsLoaded,
    setHasChanges,
    token
}) => {
    if (mode === "GROUPS") {
        const contentRatingsPayload = mapRatingSubmission({
            ratings,
            contentItems,
            existingRatingsData: derivedExistingRatingsData,
            selectedEpr,
            selectedTemplate,
            userDetails,
            groupData
        });

        const options = {
            action: "updateGroupContentRatings",
            gradingProfileId: selectedEpr.key,
            data: {
                groupInfoDto: buildGroupInfoDto(groupData, selectedEpr, selectedTemplate),
                contentRatings: contentRatingsPayload
            },
            token
        };

        const response = await ElementaryProgressReportDao(options);
        if (response?.data?.payload) {
            fetchSelectedGroupInfo();
            setRatingsLoaded(true);
            setHasChanges(false);
            toast.success("Ratings saved.", { autoClose: 3000 });
        } else {
            toast.error("Something went wrong.", { autoClose: 3000 });
            setRatingsLoaded(true);
        }
    } else {
        const payload = mapRatingSubmission({
            ratings,
            contentItems,
            existingRatingsData,
            selectedEpr,
            selectedTemplate,
            userDetails
        });

        const options = {
            action: "updateContentRatings",
            gradingProfileId: selectedEpr.key,
            gradingCriteriaId: payload[0]?.gradingCriteria,
            data: payload,
            token
        };

        const response = await ElementaryProgressReportDao(options);
        if (response?.data?.payload) {
            setExistingRatingsData(response.data.payload);
            setRatingsLoaded(true);
            setHasChanges(false);
            toast.success("Ratings saved.", { autoClose: 3000 });
        }
    }
};

const useRatingSubmission = ({
    comments,
    contentItems,
    derivedExistingRatingsData,
    existingRatingsData,
    existingStudentComments,
    fetchSelectedGroupInfo,
    groupData,
    mode,
    ratings,
    selectedEpr,
    selectedTemplate,
    setExistingRatingsData,
    setExistingStudentComments,
    setHasChanges,
    setRatingsLoaded,
    showComments,
    students,
    token,
    userDetails
}) => {
    const handleSubmit = useCallback(() => {
        setRatingsLoaded(false);

        if (showComments) {
            submitComments({
                students,
                comments,
                existingStudentComments,
                groupData,
                selectedEpr,
                selectedTemplate,
                token,
                mode,
                setExistingStudentComments,
                setRatingsLoaded,
                setHasChanges
            });
        } else {
            submitRatings({
                ratings,
                contentItems,
                existingRatingsData,
                derivedExistingRatingsData,
                selectedEpr,
                selectedTemplate,
                userDetails,
                groupData,
                mode,
                fetchSelectedGroupInfo,
                setExistingRatingsData,
                setRatingsLoaded,
                setHasChanges,
                token
            });
        }
    }, [
        comments,
        contentItems,
        derivedExistingRatingsData,
        existingRatingsData,
        existingStudentComments,
        fetchSelectedGroupInfo,
        groupData,
        mode,
        ratings,
        selectedEpr,
        selectedTemplate,
        setExistingRatingsData,
        setExistingStudentComments,
        setHasChanges,
        setRatingsLoaded,
        showComments,
        students,
        token,
        userDetails
    ]);

    return handleSubmit;
};

export default useRatingSubmission;
